import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { Severity } from '../../lib/game';
import { AlertIcon, InfoIcon, CheckIcon } from '../../icons/ui-icons';

export interface ToastProps {
  severity?: Severity;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  /**
   * Milliseconds until it dismisses itself. `0` pins it until dismissed by
   * hand — reserve that for messages the player must acknowledge.
   */
  duration?: number;
  onDismiss?: () => void;
  className?: string;
}

const SEVERITY = {
  danger: { edge: 'border-danger/70', bar: 'bg-danger', text: 'text-danger-bright', icon: AlertIcon },
  warning: { edge: 'border-warning/70', bar: 'bg-warning', text: 'text-warning-bright', icon: AlertIcon },
  success: { edge: 'border-success/70', bar: 'bg-success', text: 'text-success-bright', icon: CheckIcon },
  info: { edge: 'border-info/60', bar: 'bg-info', text: 'text-info-bright', icon: InfoIcon },
} as const;

export const TOAST_DEFAULT_DURATION = 6_000;

/**
 * A transient message that floats over the game view.
 *
 * Two things separate it from `AlertCard`, and both are the reason it exists:
 *
 *   • It is OPAQUE. An alert card sits inside a panel and can afford a 6 %
 *     colour wash over a known background. A toast floats over the orbit
 *     canvas, resource readouts and cards, so a translucent one is simply
 *     unreadable — this one paints a solid surface first and tints on top.
 *   • It dismisses itself. The countdown pauses while the pointer is over the
 *     toast or while it holds focus, so a message never disappears out from
 *     under someone who is still reading it.
 */
export function Toast({
  severity = 'info',
  title,
  description,
  icon,
  duration = TOAST_DEFAULT_DURATION,
  onDismiss,
  className,
}: ToastProps) {
  const config = SEVERITY[severity];
  const FallbackIcon = config.icon;
  const [paused, setPaused] = useState(false);

  // Remaining time survives pause/resume cycles, so hovering does not restart
  // the clock and does not lose the time already elapsed.
  const remainingRef = useRef(duration);
  const startedRef = useRef(0);

  useEffect(() => {
    remainingRef.current = duration;
  }, [duration]);

  useEffect(() => {
    if (!duration || !onDismiss || paused) return;
    startedRef.current = Date.now();
    const id = window.setTimeout(onDismiss, remainingRef.current);
    return () => {
      window.clearTimeout(id);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedRef.current));
    };
  }, [duration, onDismiss, paused]);

  return (
    <div
      role={severity === 'danger' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'relative overflow-hidden rounded-control border shadow-panel-lg',
        // Opaque base, then the severity tint — never see-through.
        'bg-surface-overlay',
        config.edge,
        'motion-safe:animate-toast-in',
        className,
      )}
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span className={cn('mt-0.5 shrink-0', config.text)}>
          {icon ?? <FallbackIcon size={18} />}
        </span>

        <div className="min-w-0 flex-1">
          <span
            className={cn(
              'font-display text-xs font-semibold uppercase tracking-[0.1em]',
              config.text,
            )}
          >
            {title}
          </span>
          {description ? (
            <p className="mt-1 text-xs leading-relaxed text-content-secondary">{description}</p>
          ) : null}
        </div>

        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Meldung schließen"
            className="shrink-0 rounded p-0.5 text-content-muted transition-colors hover:text-content"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        ) : null}
      </div>

      {duration && onDismiss ? (
        // Hairline showing the time left, so the disappearance is never a surprise.
        <div
          className={cn('h-0.5 origin-left', config.bar)}
          style={{
            animation: `sg-toast-timer ${duration}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
          aria-hidden
        />
      ) : null}
    </div>
  );
}

export interface ToastStackProps {
  children: ReactNode;
  className?: string;
}

/**
 * Fixed container for toasts.
 *
 * Sits below the top bar rather than over it — the resource readouts are
 * consulted constantly and must never be covered by a transient message.
 */
export function ToastStack({ children, className }: ToastStackProps) {
  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-3 top-16 z-toast flex flex-col gap-2',
        'sm:left-auto sm:right-4 sm:w-96',
        className,
      )}
    >
      {/* Children re-enable pointer events; the container must not eat clicks. */}
      <div className="pointer-events-auto flex flex-col gap-2">{children}</div>
    </div>
  );
}
